import { useMemo } from "react";
import { useV2ListProjectVariablesQuery } from "@generated";
import type { UserDefinedFieldRow } from "../types/UserDefinedField";

export interface UseUserDefinedFieldsListResult {
  rows: UserDefinedFieldRow[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
}

export interface UseUserDefinedFieldsListArgs {
  archived: boolean;
}

export const useUserDefinedFieldsList = ({
  archived,
}: UseUserDefinedFieldsListArgs): UseUserDefinedFieldsListResult => {
  const { data, isLoading, isFetching } = useV2ListProjectVariablesQuery({
    filter: { active: !archived },
  });

  // Memoize the rows array so its reference stays stable across renders
  // when the underlying RTK Query data hasn't changed. Without this, every
  // re-render (e.g. while a move() mutation is in flight) produces a new
  // array reference, which invalidates AdminListTable's sortedData memo
  // and triggers its data->localRows sync effect — clobbering any
  // optimistic drag-drop update.
  const rows: UserDefinedFieldRow[] = useMemo(
    () =>
      data?.data?.map((record: any) => ({
        id: String(record.id),
        position: record.attributes.position ?? 0,
        label: record.attributes.label ?? "",
        name: record.attributes.name ?? "",
        variableType: record.attributes["variable-type"] ?? "text",
        context: record.attributes["variable-context"] ?? "project",
        required: !!record.attributes.required,
        minimum: record.attributes.minimum ?? null,
        maximum: record.attributes.maximum ?? null,
        selectOptions: record.attributes["select-options"] ?? [],
        deletedAt: record.attributes["deleted-at"] ?? null,
      })) ?? [],
    [data]
  );

  return {
    rows,
    totalCount: data?.meta?.["record-count"] ?? rows.length,
    isLoading,
    isFetching,
  };
};
